import Component from "@base/Component.js";
import { sanitizeHtml, safeHref } from "@/services/richText.js";

/**
 * A small hand-written rich-text editor: a language bar over a toolbar over a
 * contenteditable region, producing exactly the markup vocabulary richText.js
 * allows. No editor library — the vocabulary is a dozen tags wide and every one
 * of them has to be on the sanitizer's allowlist anyway.
 *
 * props: { htmlByLanguage: { en, de, ... }, languages: ["en", "de"], activeLanguage, label }
 *
 * One editor serves every language: the dropdown beside the toolbar swaps which
 * translation the toolbar and text area are editing, so the form carries one set
 * of controls rather than one per language. `languages` comes from config.js, so adding a
 * language adds a tab and nothing else here. A single-language list renders no
 * tab bar at all.
 *
 * Read the result with getHtmlByLanguage(); it is sanitized on the way out,
 * just as setHtmlByLanguage() sanitizes on the way in. That is convenience and
 * defence in depth, not the security boundary — the backend sanitizes every
 * write regardless.
 *
 * Commands go through document.execCommand. It is deprecated but is still the
 * only API browsers offer for "apply this formatting to the current selection",
 * and the alternative is hand-rolling Range surgery for every command. It is
 * confined to #exec() so a future replacement is one method, not a rewrite.
 */
// Stroke-based glyphs, same idiom as the Cockpit's nav icons: 20x20, no fill,
// currentColor — so one rule colours a button and its icon together.
const icon = (paths) =>
  `<svg class="rte__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;

const ICONS = {
  bulletList: icon(`<circle cx="4" cy="6" r="1.1"/><circle cx="4" cy="10" r="1.1"/><circle cx="4" cy="14" r="1.1"/><path d="M8 6h9M8 10h9M8 14h9"/>`),
  numberList: icon(`<path d="M8 6h9M8 10h9M8 14h9"/><path d="M3 4.9 4.3 4.2v3.4"/><path d="M2.9 11.6c0-.6.5-1 1.1-1s1.2.4 1.2 1c0 1-2.3 1.5-2.3 2.7h2.4"/>`),
  link: icon(`<path d="M8.6 11.4a3.3 3.3 0 0 0 4.7 0l2-2a3.3 3.3 0 0 0-4.7-4.7l-1 1"/><path d="M11.4 8.6a3.3 3.3 0 0 0-4.7 0l-2 2a3.3 3.3 0 0 0 4.7 4.7l1-1"/>`),
  // A pulled-apart chain rather than a struck-through one: at 19px a diagonal
  // over the link glyph just reads as a second, slightly busier link.
  unlink: icon(`<path d="M12.2 7.8l2.1-2.1a3.3 3.3 0 0 1 4.7 4.7l-2.1 2.1" transform="translate(-2 -1) scale(0.88)"/><path d="M7.8 12.2l-2.1 2.1a3.3 3.3 0 0 1-4.7-4.7l2.1-2.1" transform="translate(3 1) scale(0.88)"/><path d="M13.5 3.2v2M16.8 6.5h-2M3.2 13.5h2M6.5 16.8v-2"/>`),
  chevron: icon(`<path d="M6 8.5 10 12.5l4-4"/>`),
  quote: icon(`<path d="M4.5 12.5c-1 0-1.7-.8-1.7-1.9 0-2.2 1.4-4 3.5-4.8l.5 1.1c-1.2.5-2 1.4-2.1 2.3.1 0 .2-.1.4-.1 1 0 1.7.8 1.7 1.8s-.9 1.6-2.3 1.6Z"/><path d="M12.1 12.5c-1 0-1.7-.8-1.7-1.9 0-2.2 1.4-4 3.5-4.8l.5 1.1c-1.2.5-2 1.4-2.1 2.3.1 0 .2-.1.4-.1 1 0 1.7.8 1.7 1.8s-.9 1.6-2.3 1.6Z"/>`),
  clear: icon(`<path d="M5 5.5h9"/><path d="M9.5 5.5V13"/><path d="M13 12.5 16.5 16M16.5 12.5 13 16"/>`),
};

// The size scale, smallest to largest. "" is the body default; the rest are the
// classes both sanitizers allow (services/richText.js). Ordered, because the
// stepper moves through it one notch at a time.
const SIZE_SCALE = [
  { className: "rt-size-sm", label: "S" },
  { className: "", label: "M" },
  { className: "rt-size-lg", label: "L" },
  { className: "rt-size-xl", label: "XL" },
];
const DEFAULT_SIZE_INDEX = SIZE_SCALE.findIndex((step) => step.className === "");

export default class RichTextEditor extends Component {
  // Toolbar layout: one array per visual group, rendered with a hairline
  // divider between groups. Grouping lives here rather than as separator
  // entries in a flat list, so a tool cannot end up in a group by accident.
  static TOOL_GROUPS = [
    [
      { cmd: "bold", label: "B", title: "Bold", className: "rte__btn--bold" },
      { cmd: "italic", label: "I", title: "Italic", className: "rte__btn--italic" },
      { cmd: "underline", label: "U", title: "Underline", className: "rte__btn--underline" },
      { cmd: "strikeThrough", label: "S", title: "Strikethrough", className: "rte__btn--strike" },
    ],
    [
      { block: "p", label: "¶", title: "Normal text" },
      { block: "h2", label: "H2", title: "Heading" },
      { block: "h3", label: "H3", title: "Subheading" },
      { block: "blockquote", icon: ICONS.quote, title: "Quote" },
    ],
    [
      { cmd: "insertUnorderedList", icon: ICONS.bulletList, title: "Bulleted list" },
      { cmd: "insertOrderedList", icon: ICONS.numberList, title: "Numbered list" },
    ],
    [
      { action: "link", icon: ICONS.link, title: "Insert link" },
      { action: "unlink", icon: ICONS.unlink, title: "Remove link" },
      { action: "clear", icon: ICONS.clear, title: "Remove formatting" },
    ],
  ];

  constructor(props) {
    super(props);
    this.languages = props.languages?.length ? props.languages : ["en"];
    this.htmlByLanguage = Object.fromEntries(
      this.languages.map((language) => [language, sanitizeHtml(props.htmlByLanguage?.[language] || "")])
    );
    // Honoured across a parent's re-render, so adding an image mid-edit doesn't
    // snap the admin back to the first language.
    this.activeLanguage = this.languages.includes(props.activeLanguage) ? props.activeLanguage : this.languages[0];
    // Which notch of SIZE_SCALE the stepper shows. Re-derived from the caret on
    // every selection change; this is just the starting point.
    this.sizeIndex = DEFAULT_SIZE_INDEX;
  }

  render() {
    const renderTool = (tool) => {
      const attrs = [
        tool.cmd ? `data-cmd="${tool.cmd}"` : "",
        tool.block ? `data-block="${tool.block}"` : "",
        tool.action ? `data-action="${tool.action}"` : "",
      ]
        .filter(Boolean)
        .join(" ");
      // type="button" throughout: these live inside the item editor's <form>,
      // and a default-type button would submit it on every click.
      return `<button type="button" class="rte__btn ${tool.className || ""}" ${attrs}
                title="${Component.escape(tool.title)}" aria-label="${Component.escape(tool.title)}"
              >${tool.icon || Component.escape(tool.label)}</button>`;
    };

    const sizeStepper = `
      <div class="rte__stepper" title="Text size">
        <span class="rte__stepper-value" aria-live="polite">${Component.escape(SIZE_SCALE[this.sizeIndex].label)}</span>
        <span class="rte__stepper-arrows">
          <button type="button" class="rte__stepper-arrow" data-size-step="1" aria-label="Larger text">▲</button>
          <button type="button" class="rte__stepper-arrow" data-size-step="-1" aria-label="Smaller text">▼</button>
        </span>
      </div>`;

    // A lone language has nothing to switch between, so the control is dropped
    // rather than shown as a one-entry dropdown.
    //
    // Hand-built rather than a <select>: a native one's option list is browser
    // chrome, so it cannot take this theme's gap, rounding or spacing. The
    // trade is that keyboard behaviour has to be written out (below), which a
    // <select> would have given for free.
    const languagePicker =
      this.languages.length > 1
        ? `<div class="rte__language">
             <button type="button" class="rte__language-button" aria-haspopup="listbox" aria-expanded="false" aria-label="Language">
               <span class="rte__language-current">${Component.escape(this.activeLanguage.toUpperCase())}</span>
               ${ICONS.chevron}
             </button>
             <ul class="rte__language-list" role="listbox" aria-label="Language" hidden>
               ${this.languages
                 .map((language) => {
                   const isActive = language === this.activeLanguage;
                   return `<li class="rte__language-option" role="option" tabindex="-1"
                             data-language="${Component.escape(language)}" aria-selected="${isActive}"
                           >${Component.escape(language.toUpperCase())}</li>`;
                 })
                 .join("")}
             </ul>
           </div>`
        : "";

    const groups = [sizeStepper, ...RichTextEditor.TOOL_GROUPS.map((group) => group.map(renderTool).join(""))]
      .map((content) => `<div class="rte__group">${content}</div>`)
      .join('<span class="rte__divider" aria-hidden="true"></span>');

    return `
      <div class="rte">
        ${this.props.label ? `<span class="rte__label">${Component.escape(this.props.label)}</span>` : ""}
        <div class="rte__bar">
          ${languagePicker}
          <div class="rte__toolbar" role="toolbar" aria-label="Text formatting">${groups}</div>
        </div>
        <div class="rte__body rt-content" contenteditable="true" role="textbox" aria-multiline="true"></div>
      </div>`;
  }

  onMounted() {
    this.body = this.$(".rte__body");
    // Assigned as nodes, not markup: sanitizeHtml has already vetted the value,
    // and this keeps the "never innerHTML untrusted input" rule intact.
    this.#writeBody(this.htmlByLanguage[this.activeLanguage]);

    this.#wireLanguagePicker();

    // <p> per paragraph instead of the browser's default <div>/<br> soup, so
    // what the editor produces is already in the sanitizer's vocabulary.
    document.execCommand("defaultParagraphSeparator", false, "p");

    // mousedown, not click, throughout the toolbar: a button would otherwise
    // take focus and collapse the selection before the command could apply.
    this.$$(".rte__btn").forEach((btn) => {
      this.listen(btn, "mousedown", (e) => {
        e.preventDefault();
        this.#run(btn);
      });
    });

    this.$$(".rte__stepper-arrow").forEach((arrow) => {
      this.listen(arrow, "mousedown", (e) => {
        e.preventDefault();
        this.#stepSize(Number(arrow.getAttribute("data-size-step")));
      });
    });

    this.listen(this.body, "paste", (e) => this.#onPaste(e));
    // A dropped file or dragged selection injects markup this editor never
    // produced — a blob:/data: <img> in the simplest case. There is no
    // sanitize-and-insert path worth having here; refuse the gesture instead.
    this.listen(this.body, "drop", (e) => e.preventDefault());
    this.listen(this.body, "dragover", (e) => e.preventDefault());

    const sync = () => this.#reflectState();
    this.listen(this.body, "keyup", sync);
    this.listen(this.body, "mouseup", sync);
    this.listen(this.body, "focus", sync);
    this.#reflectState();
  }

  /**
   * Open/close behaviour for the hand-built language list: click to toggle,
   * arrows and Home/End to move, Enter or Space to pick, Escape or a click
   * elsewhere to dismiss. All of this is what a native <select> would have
   * provided; it is written out here because its option list could not be
   * styled to match.
   */
  #wireLanguagePicker() {
    const button = this.$(".rte__language-button");
    const list = this.$(".rte__language-list");
    if (!button || !list) return;

    const options = this.$$(".rte__language-option");
    const setOpen = (isOpen, { focusList = false } = {}) => {
      list.hidden = !isOpen;
      button.setAttribute("aria-expanded", String(isOpen));
      if (isOpen && focusList) (options.find((o) => o.getAttribute("aria-selected") === "true") || options[0])?.focus();
      if (!isOpen) button.focus();
    };
    const isOpen = () => !list.hidden;
    const pick = (option) => {
      this.#showLanguage(option.getAttribute("data-language"));
      setOpen(false);
    };
    const moveFocus = (from, delta) => {
      const next = options[(options.indexOf(from) + delta + options.length) % options.length];
      next?.focus();
    };

    this.listen(button, "click", () => setOpen(!isOpen(), { focusList: true }));
    this.listen(button, "keydown", (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setOpen(true, { focusList: true });
      }
    });

    options.forEach((option) => {
      this.listen(option, "click", () => pick(option));
      this.listen(option, "keydown", (e) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          moveFocus(option, 1);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          moveFocus(option, -1);
        } else if (e.key === "Home") {
          e.preventDefault();
          options[0]?.focus();
        } else if (e.key === "End") {
          e.preventDefault();
          options[options.length - 1]?.focus();
        } else if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          pick(option);
        } else if (e.key === "Escape" || e.key === "Tab") {
          setOpen(false);
        }
      });
    });

    // Tracked, so it is removed with the component rather than outliving it.
    this.listen(document, "pointerdown", (e) => {
      if (isOpen() && !this.$(".rte__language").contains(e.target)) list.hidden = true;
    });
  }

  /** Every language's contents, sanitized, keyed by language code. */
  getHtmlByLanguage() {
    this.#captureActiveLanguage();
    return { ...this.htmlByLanguage };
  }

  /** Replace the contents of every language (sanitized on the way in). */
  setHtmlByLanguage(htmlByLanguage) {
    for (const language of this.languages) {
      this.htmlByLanguage[language] = sanitizeHtml(htmlByLanguage?.[language] || "");
    }
    if (this.body) this.#writeBody(this.htmlByLanguage[this.activeLanguage]);
  }

  /**
   * Move what is currently in the text area into the language it belongs to.
   * Called before every read and every tab switch — the text area is the live
   * copy of exactly one language, and the map holds the rest.
   */
  #captureActiveLanguage() {
    if (this.body) this.htmlByLanguage[this.activeLanguage] = sanitizeHtml(this.body.innerHTML);
  }

  /**
   * Swap which language the toolbar and text area are editing. A targeted DOM
   * update rather than setState(): re-rendering would rebuild the text area
   * mid-edit, and there is nothing here a full render would fix.
   */
  #showLanguage(language) {
    if (!language || language === this.activeLanguage || !this.languages.includes(language)) return;
    this.#captureActiveLanguage();
    this.activeLanguage = language;
    // Kept in step for callers that switch language in code rather than by
    // picking from the list.
    const current = this.$(".rte__language-current");
    if (current) current.textContent = language.toUpperCase();
    this.$$(".rte__language-option").forEach((option) => {
      option.setAttribute("aria-selected", String(option.getAttribute("data-language") === language));
    });
    this.#writeBody(this.htmlByLanguage[language]);
    this.body.focus();
    this.#reflectState();
  }

  #writeBody(html) {
    const doc = new DOMParser().parseFromString(`<body>${html || ""}</body>`, "text/html");
    this.body.replaceChildren(...doc.body.childNodes);
  }

  /** The single place document.execCommand is called. */
  #exec(command, argument = null) {
    this.body.focus();
    document.execCommand(command, false, argument);
    this.#reflectState();
  }

  #run(btn) {
    const cmd = btn.getAttribute("data-cmd");
    const block = btn.getAttribute("data-block");
    const action = btn.getAttribute("data-action");

    if (cmd) return this.#exec(cmd);
    if (block) return this.#exec("formatBlock", block);
    if (action === "link") return this.#link();
    if (action === "unlink") return this.#exec("unlink");
    if (action === "clear") {
      this.#exec("removeFormat");
      this.#exec("unlink");
      this.#applySize("");
      this.#showSizeIndex(DEFAULT_SIZE_INDEX);
    }
  }

  /**
   * Move the selection one notch along SIZE_SCALE. A stepper rather than one
   * button per size: four sizes is four buttons of toolbar for a setting that
   * is nearly always "leave it alone", and relative "bigger/smaller" is how the
   * choice is actually made.
   */
  #stepSize(delta) {
    const next = Math.min(SIZE_SCALE.length - 1, Math.max(0, this.#selectionSizeIndex() + delta));
    this.#applySize(SIZE_SCALE[next].className);
    this.#showSizeIndex(next);
  }

  /** Which notch the caret currently sits in — the stored one if it can't tell. */
  #selectionSizeIndex() {
    const selection = window.getSelection();
    const node = selection?.anchorNode;
    if (!node || !this.body.contains(node)) return this.sizeIndex;
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    const sized = element?.closest("span[class^='rt-size-']");
    const index = sized ? SIZE_SCALE.findIndex((step) => step.className === sized.className) : DEFAULT_SIZE_INDEX;
    return index === -1 ? DEFAULT_SIZE_INDEX : index;
  }

  /** Update the stepper's readout without re-rendering the whole toolbar. */
  #showSizeIndex(index) {
    this.sizeIndex = index;
    const value = this.$(".rte__stepper-value");
    if (value) value.textContent = SIZE_SCALE[index].label;
  }

  /**
   * Size is a class from the sanitizer's fixed set, never inline CSS — so the
   * type scale stays in main.css and nothing has to trust a style attribute.
   * execCommand has no "wrap in a class" command, so this one is hand-rolled.
   */
  #applySize(sizeClass) {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !this.body.contains(selection.anchorNode)) return;
    const range = selection.getRangeAt(0);

    // Drop any size spans already inside the selection, so switching size
    // replaces rather than nests (which would leave the old class winning).
    const contents = range.extractContents();
    contents.querySelectorAll?.("span[class^='rt-size-']").forEach((span) => span.replaceWith(...span.childNodes));

    if (sizeClass) {
      const span = document.createElement("span");
      span.className = sizeClass;
      span.append(contents);
      range.insertNode(span);
      selection.removeAllRanges();
      const next = document.createRange();
      next.selectNodeContents(span);
      selection.addRange(next);
    } else {
      range.insertNode(contents);
    }
    this.#reflectState();
  }

  #link() {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) {
      window.alert("Select the text you want to turn into a link first.");
      return;
    }
    const input = window.prompt("Link URL (http, https or mailto):", "https://");
    if (!input) return;
    const href = safeHref(input);
    if (!href) {
      window.alert("That link was not added: only http, https and mailto addresses are allowed.");
      return;
    }
    this.#exec("createLink", href);
    // execCommand writes a bare <a href>; rel/target are ours to set, and the
    // sanitizer would add them on save anyway — do it now so the editor shows
    // the same element that gets stored.
    this.body.querySelectorAll(`a[href="${CSS.escape(href)}"]`).forEach((a) => {
      a.setAttribute("rel", "noopener noreferrer nofollow");
      a.setAttribute("target", "_blank");
    });
  }

  /**
   * Pasted content never reaches the document as-is: HTML goes through the same
   * allowlist the renderer uses (Word and browser copy carry style attributes,
   * font tags and occasionally script), and anything else lands as plain text.
   */
  #onPaste(e) {
    e.preventDefault();
    const html = e.clipboardData?.getData("text/html");
    const text = e.clipboardData?.getData("text/plain") || "";
    const clean = html ? sanitizeHtml(html) : "";
    if (clean) document.execCommand("insertHTML", false, clean);
    else if (text) document.execCommand("insertText", false, text);
    this.#reflectState();
  }

  /** Light up the toolbar buttons that apply to the current selection. */
  #reflectState() {
    this.#showSizeIndex(this.#selectionSizeIndex());
    for (const btn of this.$$(".rte__btn[data-cmd]")) {
      let active = false;
      try {
        active = document.queryCommandState(btn.getAttribute("data-cmd"));
      } catch {
        active = false; // not every command answers on every browser
      }
      btn.classList.toggle("rte__btn--active", active);
      btn.setAttribute("aria-pressed", String(active));
    }
  }
}
